import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Box, CircularProgress, Paper, Typography, Alert, Button } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import { completeIESOAuth } from "../../services/api/iesService";

/**
 * Landing page for the Intuit consent redirect.
 *
 * Intuit ties a production app to its registered domain, so the redirect lands
 * here (the portal) while the token exchange happens on the API. This page
 * forwards the parameters and reports the outcome — including the realm id,
 * which is what the operator needs to finish the setup.
 */
const IESOAuthCallback = () => {
  const [params] = useSearchParams();
  const [state, setState] = useState<"working" | "done" | "error">("working");
  const [realmId, setRealmId] = useState<string | null>(null);
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    const code = params.get("code");
    const oauthState = params.get("state");
    const realm = params.get("realmId");
    const providerError = params.get("error");

    if (providerError) {
      setState("error");
      setMessage(`Intuit returned an error: ${providerError}`);
      return;
    }
    if (!code || !oauthState || !realm) {
      setState("error");
      setMessage(
        "Missing code, state or realmId in the callback. Start the connection again from the Integrations page."
      );
      return;
    }

    completeIESOAuth(code, oauthState, realm)
      .then((res: any) => {
        setRealmId(res?.realmId || realm);
        setState("done");
      })
      .catch((err: any) => {
        setState("error");
        setMessage(err?.message || "Failed to complete the Intuit connection.");
      });
  }, [params]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 560, borderRadius: 0 }} variant="outlined">
        {state === "working" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CircularProgress size={22} />
            <Typography sx={{ fontSize: 15 }}>
              Completing the Intuit connection…
            </Typography>
          </Box>
        )}

        {state === "done" && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <CheckCircleIcon color="success" />
              <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
                Realm connected
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 1, fontSize: 14 }}>
              Realm id:{" "}
              <Box component="code" sx={{ fontWeight: 700 }}>
                {realmId}
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              The realm's tokens are stored on the server and the realm is registered
              automatically. Enable IES on the Integrations page and run the sync to pull
              its data in.
            </Typography>
            <Button
              sx={{ mt: 2, }}
              variant="contained"
              href="/dashboard/integrations"
            >
              Go to Integrations
            </Button>
          </>
        )}

        {state === "error" && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <ErrorIcon color="error" />
              <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
                Could not complete the connection
              </Typography>
            </Box>
            <Alert severity="error" sx={{ borderRadius: 0 }}>
              {message}
            </Alert>
            <Button
              sx={{ mt: 2, }}
              variant="outlined"
              href="/dashboard/integrations"
            >
              Back to Integrations
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default IESOAuthCallback;
